<div class="dashboard-container">
  <!-- Header Section -->
  <div class="dashboard-header">
    <div class="header-content">
      <h1 class="dashboard-title">
        <i class="fas fa-chart-line"></i>
        Scrap Collection Insights
      </h1>
      <p class="dashboard-subtitle">Analyze and track scrap collection performance</p>
    </div>
    <button class="btn-export" (click)="exportToCSV()" [disabled]="!insights || loading">
      <i class="fas fa-download"></i>
      Export to CSV
    </button>
  </div>

  <!-- Filters Section -->
  <div class="filters-section">
    <div class="filters-card">
      <h3 class="filters-title">
        <i class="fas fa-filter"></i>
        Filters
      </h3>

      <div class="filters-grid">
        <!-- Agent Filter -->
        <div class="filter-group">
          <label for="agentFilter" class="filter-label">
            <i class="fas fa-user"></i>
            Agent
          </label>
          <div class="status-dropdown-container">
            <button
              type="button"
              id="agentFilter"
              (click)="toggleAgentDropdown()"
              class="status-dropdown-button"
              [class.active]="agentDropdownOpen || selectedAgents.length > 0">
              <span>{{ getAgentDisplayText() }}</span>
              <i class="fas" [class.fa-chevron-down]="!agentDropdownOpen" [class.fa-chevron-up]="agentDropdownOpen"></i>
            </button>
            <div class="status-dropdown-menu" *ngIf="agentDropdownOpen">
              <div class="status-dropdown-header">
                <span>Select Agent(s)</span>
                <button 
                  type="button"
                  (click)="selectedAgents = []; $event.stopPropagation()"
                  class="clear-status-btn"
                  *ngIf="selectedAgents.length > 0">
                  Clear All
                </button>
              </div>
              <div class="status-options">
                <label 
                  *ngFor="let agent of agents" 
                  class="status-option-item"
                  (click)="$event.stopPropagation()">
                  <input
                    type="checkbox"
                    [checked]="isAgentSelected(agent.value)"
                    (change)="toggleAgent(agent.value)"
                    class="status-checkbox">
                  <span class="status-label">{{ agent.label }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- GeoZone Filter -->
        <div class="filter-group">
          <label for="zoneFilter" class="filter-label">
            <i class="fas fa-map-marker-alt"></i>
            Zone
          </label>
          <app-district-zone-filter
            [(ngModel)]="selectedGeoZones">
          </app-district-zone-filter>
        </div>

        <!-- From Date Filter -->
        <div class="filter-group">
          <label for="fromDate" class="filter-label">
            <i class="fas fa-calendar-alt"></i>
            From Date
          </label>
          <input type="date" id="fromDate" [(ngModel)]="fromDate" class="filter-input">
        </div>

        <!-- To Date Filter -->
        <div class="filter-group">
          <label for="toDate" class="filter-label">
            <i class="fas fa-calendar-check"></i>
            To Date
          </label>
          <input type="date" id="toDate" [(ngModel)]="toDate" class="filter-input">
        </div>
      </div>

      <div class="filters-actions">
        <button class="btn-secondary" (click)="resetFilters()" [disabled]="loading">
          <i class="fas fa-redo"></i>
          Reset
        </button>
        <button class="btn-primary" (click)="applyFilters()" [disabled]="loading">
          <i class="fas fa-search"></i>
          Apply Filters
        </button>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div class="loading-container" *ngIf="loading">
    <div class="spinner"></div>
    <p class="loading-text">Loading insights...</p>
  </div>

  <!-- Error State -->
  <div class="error-container" *ngIf="errorMessage && !loading">
    <div class="error-card">
      <i class="fas fa-exclamation-triangle"></i>
      <h3>Error Loading Insights</h3>
      <p>{{ errorMessage }}</p>
      <button class="btn-primary" (click)="loadInsights()">
        <i class="fas fa-redo"></i>
        Try Again
      </button>
    </div>
  </div>

  <!-- Dashboard Content -->
  <div class="dashboard-content" *ngIf="insights && !loading">
    <!-- Summary Cards -->
    <div class="summary-cards">
      <div class="summary-card card-collections">
        <div class="card-icon">
          <i class="fas fa-box"></i>
        </div>
        <div class="card-content">
          <h4 class="card-title">Total Collections</h4>
          <p class="card-value">{{ insights.totalCollections }}</p>
        </div>
      </div>

      <div class="summary-card card-revenue">
        <div class="card-icon">
          <i class="fas fa-rupee-sign"></i>
        </div>
        <div class="card-content">
          <h4 class="card-title">Grand Total</h4>
          <p class="card-value">{{ formatCurrency(insights.totalGrandTotal) }}</p>
        </div>
      </div>

      <div class="summary-card card-scrap-value">
        <div class="card-icon">
          <i class="fas fa-recycle"></i>
        </div>
        <div class="card-content">
          <h4 class="card-title">Total Scrap Value</h4>
          <p class="card-value">{{ formatCurrency(insights.totalScrapValue) }}</p>
        </div>
      </div>

      <div class="summary-card card-non-scrap-value">
        <div class="card-icon">
          <i class="fas fa-boxes"></i>
        </div>
        <div class="card-content">
          <h4 class="card-title">Total Non-Scrap Value</h4>
          <p class="card-value">{{ formatCurrency(insights.totalNonScrapValue) }}</p>
        </div>
      </div>

      <div class="summary-card card-platform">
        <div class="card-icon">
          <i class="fas fa-percentage"></i>
        </div>
        <div class="card-content">
          <h4 class="card-title">Platform Charges</h4>
          <p class="card-value">{{ formatCurrency(insights.totalPlatformCharges) }}</p>
        </div>
      </div>

      <div class="summary-card card-collection">
        <div class="card-icon">
          <i class="fas fa-truck"></i>
        </div>
        <div class="card-content">
          <h4 class="card-title">Collection Charges</h4>
          <p class="card-value">{{ formatCurrency(insights.totalCollectionCharges) }}</p>
        </div>
      </div>

      <div class="summary-card card-voucher-commission">
        <div class="card-icon">
          <i class="fas fa-ticket-alt"></i>
        </div>
        <div class="card-content">
          <h4 class="card-title">Voucher Commission</h4>
          <p class="card-value">{{ formatCurrency(insights.totalVoucherCommission) }}</p>
        </div>
      </div>

      <div class="summary-card card-agent-commission">
        <div class="card-icon">
          <i class="fas fa-user-tag"></i>
        </div>
        <div class="card-content">
          <h4 class="card-title">Agent Voucher Commission</h4>
          <p class="card-value">{{ formatCurrency(insights.totalAgentVoucherCommission) }}</p>
        </div>
      </div>

      <div class="summary-card card-lead-generation-fee">
        <div class="card-icon">
          <i class="fas fa-bullhorn"></i>
        </div>
        <div class="card-content">
          <h4 class="card-title">Lead Generation Fee</h4>
          <p class="card-value">{{ formatCurrency(insights.totalLeadGenerationFee || 0) }}</p>
        </div>
      </div>
    </div>

    <!-- Scrap Items Section -->
    <div class="scrap-items-section">
      <div class="section-header">
        <div class="section-title-group">
          <h2 class="section-title">
            <i class="fas fa-list"></i>
            Scrap Item Insights
          </h2>
          <p class="section-subtitle">Detailed breakdown by scrap item type</p>
        </div>

        <div class="section-controls">
          <div class="sort-controls">
            <label class="sort-label">Sort by:</label>
            <button class="sort-btn" [class.active]="sortBy === 'totalValue'" (click)="changeSortBy('totalValue')">
              Value
              <i class="fas"
                [ngClass]="sortBy === 'totalValue' ? (sortDirection === 'desc' ? 'fa-arrow-down' : 'fa-arrow-up') : 'fa-sort'"></i>
            </button>
            <button class="sort-btn" [class.active]="sortBy === 'netQuantity'" (click)="changeSortBy('netQuantity')">
              Quantity
              <i class="fas"
                [ngClass]="sortBy === 'netQuantity' ? (sortDirection === 'desc' ? 'fa-arrow-down' : 'fa-arrow-up') : 'fa-sort'"></i>
            </button>
            <button class="sort-btn" [class.active]="sortBy === 'collectionCount'"
              (click)="changeSortBy('collectionCount')">
              Collections
              <i class="fas"
                [ngClass]="sortBy === 'collectionCount' ? (sortDirection === 'desc' ? 'fa-arrow-down' : 'fa-arrow-up') : 'fa-sort'"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Scrap Items Table -->
      <div class="table-container">
        <table class="insights-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Scrap Item</th>
              <th>Unit</th>
              <th class="text-right">Total Qty</th>
              <th class="text-right">Deducted</th>
              <th class="text-right">Net Qty</th>
              <th class="text-right">Total Value</th>
              <th class="text-right">Collections</th>
              <th class="text-center">% of Total</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let item of getSortedScrapItems(); let i = index" class="table-row">
              <td class="row-number">{{ i + 1 }}</td>
              <td class="item-name">
                <div class="item-name-container">
                  <div class="item-icon">
                    <i class="fas fa-recycle"></i>
                  </div>
                  <span>{{ item.scrapItemName }}</span>
                </div>
              </td>
              <td class="unit-badge">
                <span class="badge">{{ item.unit }}</span>
              </td>
              <td class="text-right">{{ formatNumber(item.totalQuantity) }}</td>
              <td class="text-right deducted-value">
                <span *ngIf="item.totalDeductedQuantity > 0" class="badge badge-warning">
                  -{{ formatNumber(item.totalDeductedQuantity) }}
                </span>
                <span *ngIf="item.totalDeductedQuantity === 0">-</span>
              </td>
              <td class="text-right net-quantity">{{ formatNumber(item.netQuantity) }}</td>
              <td class="text-right total-value">{{ formatCurrency(item.totalValue) }}</td>
              <td class="text-right">
                <span class="badge badge-info">{{ item.collectionCount }}</span>
              </td>
              <td class="text-center">
                <div class="percentage-container">
                  <div class="percentage-bar">
                    <div class="percentage-fill" [style.width.%]="getPercentageOfTotal(item.totalValue)">
                    </div>
                  </div>
                  <span class="percentage-text">{{ getPercentageOfTotal(item.totalValue).toFixed(1) }}%</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div class="empty-state" *ngIf="!insights.scrapItemInsights || insights.scrapItemInsights.length === 0">
        <i class="fas fa-inbox"></i>
        <h3>No Data Available</h3>
        <p>No scrap item insights found for the selected filters.</p>
      </div>
    </div>
  </div>

  <!-- Empty State (No Data at All) -->
  <div class="empty-state-main" *ngIf="!insights && !loading && !errorMessage">
    <i class="fas fa-chart-bar"></i>
    <h3>No Insights Data</h3>
    <p>Apply filters above to load scrap collection insights.</p>
  </div>
</div>
